import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface Goal {
  _id?: string;
  id?: string;
  projectId: string;
  milestoneId?: string | {
    _id: string;
    title: string;
    status: string;
    scheduledDate?: string;
  };
  milestone?: {
    _id: string;
    title: string;
    status: string;
    scheduledDate?: string;
  };
  deletedAt?: string | Date;
  title: string;
  description?: string;
  targetValue: number;
  currentValue: number;
  unit: string;
  progress: number;
  status: 'pending' | 'in_progress' | 'reached' | 'failed' | 'delayed' | 'postponed' | 'canceled';
  outcome?: 'pending' | 'met' | 'exceeded' | 'not_met';
  outcomeNotes?: string;
  conclusion?: string;
  results?: string;
  delayReason?: string;
  postponeReason?: string;
  cancelReason?: string;
  updates?: Array<{
    updateId: string;
    updatedAt: string;
    updatedBy: {
      _id: string;
      displayName: string;
      email: string;
    };
    previousValue: number;
    newValue: number;
    notes?: string;
    statusChange?: string;
  }>;
  createdBy?: {
    _id: string;
    displayName: string;
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface GoalsState {
  items: Record<string, Goal>;
  projectGoals: Record<string, string[]>; // projectId -> goalIds[]
  isLoading: boolean;
  error: string | null;
}

const initialState: GoalsState = {
  items: {},
  projectGoals: {},
  isLoading: false,
  error: null,
};

// Fetch goals for a project
export const fetchProjectGoals = createAsyncThunk(
  'goals/fetchProjectGoals',
  async ({ projectId, includeDeleted = false }: { projectId: string; includeDeleted?: boolean }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { 
          headers: { Authorization: `Bearer ${state.auth.accessToken}` },
          params: includeDeleted ? { includeDeleted: 'true' } : {}
        }
      : {};
    const response = await axios.get(`${API_BASE_URL}/goals/project/${projectId}`, config);
    return { projectId, goals: response.data.data.goals };
  }
);

// Fetch single goal
export const fetchGoalById = createAsyncThunk(
  'goals/fetchGoalById',
  async (goalId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
      : {};
    const response = await axios.get(`${API_BASE_URL}/goals/${goalId}`, config);
    return response.data.data.goal;
  }
);

// Create goal
export const createGoal = createAsyncThunk(
  'goals/create',
  async (data: Partial<Goal>, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(`${API_BASE_URL}/goals`, data, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.goal;
  }
);

// Update goal
export const updateGoal = createAsyncThunk(
  'goals/update',
  async ({ id, data }: { id: string; data: Partial<Goal> }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.put(`${API_BASE_URL}/goals/${id}`, data, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.goal;
  }
);

// Delete goal
export const deleteGoal = createAsyncThunk(
  'goals/delete',
  async (goalId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    await axios.delete(`${API_BASE_URL}/goals/${goalId}`, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return goalId;
  }
);

// Restore goal
export const restoreGoal = createAsyncThunk(
  'goals/restore',
  async (goalId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(`${API_BASE_URL}/goals/${goalId}/restore`, {}, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.goal;
  }
);

const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    clearGoals: (state) => {
      state.items = {};
      state.projectGoals = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectGoals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectGoals.fulfilled, (state, action) => {
        state.isLoading = false;
        const goalIds: string[] = [];
        action.payload.goals.forEach((goal: Goal) => {
          const id = goal._id || goal.id || '';
          if (id) {
            state.items[id] = goal;
            goalIds.push(id);
          }
        });
        state.projectGoals[action.payload.projectId] = goalIds;
      })
      .addCase(fetchProjectGoals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch goals';
      })
      .addCase(fetchGoalById.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
        }
      })
      .addCase(createGoal.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
          const projectId = action.payload.projectId;
          if (projectId && !state.projectGoals[projectId]) {
            state.projectGoals[projectId] = [];
          }
          if (projectId && state.projectGoals[projectId]) {
            state.projectGoals[projectId].push(id);
          }
        }
      })
      .addCase(updateGoal.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
        }
      })
      .addCase(deleteGoal.fulfilled, (state, action) => {
        const goalId = action.payload;
        delete state.items[goalId];
        // Remove from project goals
        Object.keys(state.projectGoals).forEach((projectId) => {
          state.projectGoals[projectId] = state.projectGoals[projectId].filter(
            (id) => id !== goalId
          );
        });
      });
  },
});

export const { clearGoals } = goalsSlice.actions;
export default goalsSlice.reducer;

